import React, { useCallback, useEffect, useMemo } from 'react';
import { initAeroSyncWidget, WidgetConfigs } from 'aerosync-web-sdk';
import { getLogger } from 'src/utils/datadog-logger';
import { useDutchiePayAnalytics } from 'src/payments/hooks';
import _ from 'lodash';
import {
  AEROSYNC_CONFIGURATION_ID,
  AEROSYNC_ENVIRONMENT,
  AEROSYNC_WIDGET_STYLE,
} from 'src/payments/hooks/use-aerosync-widget';

type AerosyncMfaWidgetProps = {
  aeroPassUserUuid: string;
  token: string;
  jobId: string;
  connectionId: string;
  onSuccess: WidgetConfigs['onSuccess'];
  onClose: WidgetConfigs['onClose'];
  onError: WidgetConfigs['onError'];
};

const logger = getLogger('aerosync-mfa-widget');
const AEROSYNC_MFA_WIDGET_ID = 'aerosync-mfa-widget';

export const AerosyncMfaWidget: React.FC<AerosyncMfaWidgetProps> = ({
  aeroPassUserUuid,
  token,
  jobId,
  connectionId,
  onSuccess,
  onClose,
  onError,
}) => {
  const { trackAeroSyncOnEvent } = useDutchiePayAnalytics();
  const onEvent = useCallback(trackAeroSyncOnEvent, [trackAeroSyncOnEvent]);
  const onLoad = useCallback(() => _.noop(), []);

  const aerosyncWidgetOptions: WidgetConfigs = useMemo(
    () => ({
      configurationId: AEROSYNC_CONFIGURATION_ID,
      environment: AEROSYNC_ENVIRONMENT,
      style: AEROSYNC_WIDGET_STYLE,
      elementId: AEROSYNC_MFA_WIDGET_ID,
      iframeTitle: 'Aerosync MFA',
      zIndex: '1000',
      token,
      handleMFA: true,
      jobId,
      connectionId,
      aeroPassUserUuid,
      onEvent,
      onSuccess,
      onClose,
      onError,
      onLoad,
    }),
    [token, jobId, connectionId, aeroPassUserUuid, onEvent, onSuccess, onClose, onError, onLoad]
  );

  useEffect(() => {
    const widgetRef = initAeroSyncWidget(aerosyncWidgetOptions);
    widgetRef.launch();

    const cleanup = (): void => {
      try {
        widgetRef.destroy();
      } catch (error) {
        logger.error('Aerosync mfa widget error', { error });
      }
    };

    return cleanup;
  }, [aerosyncWidgetOptions]);

  return <div id={AEROSYNC_MFA_WIDGET_ID} />;
};
